import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  addGrouping,
  addGroupingHistory,
  deleteGrouping,
  deleteGroupingHistory,
  exportFromFile,
  getGroupingDetail,
  getGroupingDetails,
  getGroupingHistoryDetail,
  importFromFile,
  updateGrouping,
  updateGroupingHistory,
} from "./service";
import { FormGrouping } from "../form/InsertGrouping";
import { useToast } from "../../../context/toast/ToastContext";
import { FormGroupingHistory } from "../form/InsertHistoryGrouping";
import { GetCurrentDate } from "../../../utils/Helper";

export const useGetGroupingDetails = ({
  product_id,
  page,
  perPage,
}: {
  product_id?: number;
  page: number;
  perPage: number;
}) => {
  return useQuery({
    queryKey: ["getGroupingDetails", product_id, perPage, page],
    queryFn: () => getGroupingDetails({ product_id, page, perPage }),
  });
};

export const useGetGroupingDetail = ({ id }: { id?: number }) => {
  return useQuery({
    enabled: id !== undefined,
    queryKey: ["getGroupingDetail", id],
    queryFn: () => getGroupingDetail({ id }),
  });
};

export const useGetHistoryGroupingDetail = ({
  grouping_product_id,
  page,
  perPage,
  per_page,
}: {
  grouping_product_id?: number;
  perPage?: number;
  per_page?: number;
  page?: number;
}) => {
  return useQuery({
    queryKey: [
      "getGroupingHistoryDetail",
      grouping_product_id,
      page,
      perPage,
      per_page,
    ],
    queryFn: () =>
      getGroupingHistoryDetail({
        grouping_product_id,
        per_page,
        perPage,
        page,
      }),
  });
};

export const useAddGrouping = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      current_stock,
      initial_stock,
      product_id,
      status,
      group_name,
    }: FormGrouping) =>
      addGrouping({
        current_stock,
        initial_stock,
        product_id,
        status,
        group_name,
      }),
    onSuccess: (e) => {
      if (e.data.statusCode === 200) {
        toast.show("Berhasil menambahkan Grouping baru");
        queryClient.invalidateQueries();
      } else if (e.data.statusCode === 400) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      } else if (e.data.statusCode === 101) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      }
    },
  });
};

export const useAddHistoryGrouping = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      grouping_product_id,
      notes,
      quantity,
      status,
    }: FormGroupingHistory) =>
      addGroupingHistory({ grouping_product_id, notes, quantity, status }),
    onSuccess: (e) => {
      if (e.data.statusCode === 200) {
        toast.show("Berhasil menambahkan History Grouping Baru");
        queryClient.invalidateQueries();
      } else if (e.data.statusCode === 400) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      } else if (e.data.statusCode === 101) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      }
    },
  });
};

export const useUpdateGrouping = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      current_stock,
      status,
      group_name,
    }: Omit<FormGrouping, "initial_stock" | "product_id"> & {
      id: number;
    }) => updateGrouping({ id, current_stock, status, group_name }),
    onSuccess: (e) => {
      if (e.data.statusCode === 200) {
        toast.show("Berhasil update Grouping");
        queryClient.invalidateQueries();
      } else if (e.data.statusCode === 400) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      } else if (e.data.statusCode === 101) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      }
    },
  });
};

export const useDeleteGrouping = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number }) => deleteGrouping({ id }),
    onSuccess: (e) => {
      if (e.data.statusCode === 200) {
        toast.show("Berhasil delete Grouping");
        queryClient.invalidateQueries();
      } else if (e.data.statusCode === 400) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      } else if (e.data.statusCode === 101) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      }
    },
  });
};

export const useUpdateHistoryGrouping = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      notes,
      quantity,
      status,
    }: Omit<FormGroupingHistory, "grouping_product_id"> & { id: number }) =>
      updateGroupingHistory({ id, notes, quantity, status }),
    onSuccess: (e) => {
      if (e.data.statusCode === 200) {
        toast.show("Berhasil update History Grouping");
        queryClient.invalidateQueries();
      } else if (e.data.statusCode === 400) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      } else if (e.data.statusCode === 101) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      }
    },
  });
};

export const useDeleteHistoryGrouping = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number }) => deleteGroupingHistory({ id }),
    onSuccess: (e) => {
      if (e.data.statusCode === 200) {
        toast.show("Berhasil delete History Grouping");
        queryClient.invalidateQueries();
      } else if (e.data.statusCode === 400) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      } else if (e.data.statusCode === 101) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      }
    },
  });
};

export const useExportFromFile = () => {
  return useMutation({
    mutationFn: () => exportFromFile(),
    onSuccess: (e) => {
      const csv = e.data;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;

      a.download = `data-grouping-product-${GetCurrentDate()}.csv`;
      a.style.display = "none";

      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
};

export const useImportFromFile = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file }: { file: File }) => importFromFile({ file }),
    onSuccess: (e) => {
      if (e.data.statusCode === 200) {
        toast.show("Berhasil import file");
        queryClient.invalidateQueries();
      } else if (e.data.statusCode === 400) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      } else if (e.data.statusCode === 101) {
        const msg =
          e.data.data || "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      }
    },
  });
};
